<template>
  <Layout>
    <div class="lg:flex lg:h-screen lg:flex-col">
      <div class="sm:flex sm:justify-between">
        <router-link to="/contacts">
          <TButtonSvg label="List View" color="white" @click="previousMonth()">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
              />
            </svg>
          </TButtonSvg>
        </router-link>
        <div>
          <div class="flex justify-center">
            <div class="text-lg font-semibold text-gray-900">{{ formatDate(currentMonth, 'MMMM y') }}</div>
          </div>
          <div class="mt-2 mb-2 flex justify-center">
            <span class="group flex px-2 text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"
              >Phone Screen</span
            >
            <span class="ml-2 group flex px-2 text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
              >Interview</span
            >
            <span
              class="ml-2 group flex px-2 text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800"
              >Pipeline Review</span
            >
          </div>
        </div>
        <div>
          <TButton label="Previous Month" color="white" @click="previousMonth()" />
          <TButton class="sm:ml-2" label="Next Month" color="white" @click="nextMonth()" />
        </div>
      </div>
      <div class="mt-2 shadow border border-gray-200 lg:flex lg:flex-auto lg:flex-col">
        <div
          class="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none"
        >
          <div class="flex justify-center bg-white py-2">
            <span>S</span>
            <span class="sr-only sm:not-sr-only">un</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>M</span>
            <span class="sr-only sm:not-sr-only">on</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>T</span>
            <span class="sr-only sm:not-sr-only">ue</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>W</span>
            <span class="sr-only sm:not-sr-only">ed</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>T</span>
            <span class="sr-only sm:not-sr-only">hu</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>F</span>
            <span class="sr-only sm:not-sr-only">ri</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>S</span>
            <span class="sr-only sm:not-sr-only">at</span>
          </div>
        </div>
        <div class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
          <div class="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
            <div
              v-for="(day, index) of calendarDays"
              :key="day.toString() + index"
              :class="
                isDateSame(day, currentMonth, 'month')
                  ? 'relative bg-white py-2 px-3 hover:bg-gray-100'
                  : 'relative bg-gray-50 py-2 px-3 text-gray-500 hover:bg-gray-100'
              "
            >
              <div
                :class="
                  isDateSame(day, currentMonth, 'month') && isDateSame(day, currentDay, 'day')
                    ? 'flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white'
                    : ''
                "
              >
                {{ formatDate(day, 'd') }}
              </div>
              <ol v-for="event of events" :key="event.id" class="mt-2">
                <li v-if="isDateSame(day, event.start_time, 'day') && isDateSame(day, currentMonth, 'month')">
                  <router-link
                    :to="
                      event.contact.record_type == 'Teammate'
                        ? `/team/${event.contact_id}`
                        : `/contacts/${event.contact_id}`
                    "
                    class="group flex px-2 text-xs leading-5 font-semibold rounded-full"
                    :class="{
                      'bg-purple-100 text-purple-800': event.event_type == 'pipeline_review',
                      'bg-blue-100 text-blue-800': event.event_type == 'phone_screen',
                      'bg-green-100 text-green-800': event.event_type == 'interview',
                    }"
                  >
                    <p class="flex-auto truncate">{{ event.contact.name }}</p>
                    <div class="ml-3 hidden flex-none xl:block">{{ formatDate(event.start_time, 'h:mm a') }}</div>
                  </router-link>
                </li>
              </ol>
            </div>
          </div>
        </div>
        <div class="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
          <button
            v-for="(day, index) of calendarDays"
            :key="day.toString() + index"
            type="button"
            :class="
              isDateSame(day, currentMonth, 'month')
                ? 'flex h-14 flex-col bg-white py-2 px-3 text-gray-900 hover:bg-gray-100 focus:z-10'
                : 'flex h-14 flex-col bg-gray-50 py-2 px-3 text-gray-500 hover:bg-gray-100 focus:z-10'
            "
            @click="currentDay = day"
          >
            <div
              :class="
                isDateSame(day, currentMonth, 'month') && isDateSame(day, currentDay, 'day')
                  ? 'font-semibold text-indigo-600 ml-auto'
                  : 'ml-auto'
              "
            >
              {{ formatDate(day, 'd') }}
            </div>
            <span v-for="event of events" :key="event.id" class="-mx-0.5 mt-auto flex flex-wrap-reverse">
              <span
                v-if="isDateSame(day, event.start_time, 'day') && isDateSame(day, currentMonth, 'month')"
                class="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full"
                :class="{
                  'bg-purple-800': event.event_type == 'pipeline_review',
                  'bg-blue-800': event.event_type == 'phone_screen',
                  'bg-green-800': event.event_type == 'interview',
                }"
              >
              </span>
            </span>
          </button>
        </div>
      </div>
      <div class="py-10 px-4 sm:px-6 lg:hidden">
        <ol class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow border border-gray-200">
          <li
            v-for="event of events"
            :key="event.id"
            :class="
              isDateSame(currentDay, event.start_time, 'day') && isDateSame(currentDay, currentMonth, 'month')
                ? 'group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50'
                : ''
            "
          >
            <div
              v-if="isDateSame(currentDay, event.start_time, 'day') && isDateSame(currentDay, currentMonth, 'month')"
              class="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50"
            >
              <div class="flex-auto">
                <p class="font-semibold text-gray-900">{{ event.contact.name }} - {{ event.name }}</p>
                <div class="flex">
                  <svg
                    class="mr-2 h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  {{ formatDate(event.start_time, 'h:mm a') }} - {{ formatDate(event.end_time, 'h:mm a') }}
                </div>
              </div>
              <router-link
                :to="
                  event.contact.record_type == 'Teammate'
                    ? `/team/${event.contact_id}`
                    : `/contacts/${event.contact_id}`
                "
                class="ml-6 flex-none self-center rounded-md border border-gray-300 bg-white py-2 px-3 font-semibold text-gray-700 opacity-0 shadow-sm hover:bg-gray-50 focus:opacity-100 group-hover:opacity-100"
                >View</router-link
              >
            </div>
          </li>
        </ol>
      </div>
    </div>
  </Layout>
</template>

<script>
import { startOfMonth, startOfWeek, endOfMonth, endOfWeek, addMonths } from 'date-fns';
import Layout from '../Layout/index.vue';
import DateUtility from '../../utilities/dates';

export default {
  components: {
    Layout,
  },
  data() {
    return {
      calendarDays: [],
      events: [],
      currentMonth: new Date(),
      currentDay: new Date(),
    };
  },
  mounted() {
    this.getDays();
    this.getEvents();
  },
  methods: {
    ...DateUtility,
    getDays() {
      let firstDayOfMonth = startOfMonth(this.currentMonth);
      let firstDayOfCal = startOfWeek(firstDayOfMonth);
      let lastDayOfMonth = endOfMonth(firstDayOfMonth);
      let lastDayOfCal = endOfWeek(lastDayOfMonth);
      let temp = new Date(firstDayOfCal);
      let days = [temp];
      while (temp < lastDayOfCal && days.length < 42) {
        temp.setDate(temp.getDate() + 1);
        days.push(new Date(temp));
      }
      while (days.length < 42) {
        temp.setDate(temp.getDate() + 1);
        days.push(new Date(temp));
      }
      this.calendarDays = days;
    },
    getEvents() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/contact_events`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.events = data;
        });
    },
    nextMonth() {
      this.currentMonth = addMonths(this.currentMonth, 1);
      this.getDays();
    },
    previousMonth() {
      this.currentMonth = addMonths(this.currentMonth, -1);
      this.getDays();
    },
  },
};
</script>
